import React from "react";
import { api } from "../api";

// Centralized compliance copy — used by the first-login disclosure modal,
// the Disclosures page, and the global footer. Friendly tone (per AP voice)
// while still satisfying the substance of CA-RIA + federal disclosure norms.

// NEUTRAL generic fallback shown only for the brief moment before
// /firm-settings/public resolves THIS firm's real identity (see
// useFirmIdentity below). Deliberately carries NO firm's real values — a
// non-AP firm must never flash "Abundance Pathways" on first paint, and AP's
// own instance resolves to its real identity via the API a moment later.
export const FIRM_INFO = {
  legal_name: "Your Firm",
  short_name: "Your Firm",
  state: "",
  crd: "",
  address: "",
  phone: "",
  email: "",
  website: "",
  iapd_url: "https://adviserinfo.sec.gov/",
  cco_name: "",
  social_url: "",
  social_label: "",
  logo: "",
};

type FirmIdentity = typeof FIRM_INFO & { advUrl: string; privacyUrl: string };

// Single source of truth for firm identity + disclosure URLs on the
// frontend: GET /firm-settings/public (returns an `identity` block resolved
// from the active MethodologyProfile, with env fallback). FIRM_INFO is only
// the generic fallback if the API hasn't loaded yet. Every firm-identity
// render path uses this hook so a new firm shows ITS identity with no code
// edits. (advisor-uploaded Form ADV / Privacy PDFs override the IAPD link.)
export function useFirmIdentity(): FirmIdentity {
  const [id, setId] = React.useState<FirmIdentity>({ ...FIRM_INFO, cco_name: "", social_url: "", social_label: "", logo: "", advUrl: FIRM_INFO.iapd_url, privacyUrl: "/privacy" });
  React.useEffect(() => {
    let alive = true;
    api.get("/firm-settings/public")
      .then((d: any) => {
        if (!alive) return;
        const i = d?.identity || {};
        setId({
          legal_name: i.legal_name || FIRM_INFO.legal_name,
          short_name: i.short_name || FIRM_INFO.short_name,
          state:      i.state || FIRM_INFO.state,
          crd:        i.crd || FIRM_INFO.crd,
          address:    i.address || FIRM_INFO.address,
          phone:      i.phone || FIRM_INFO.phone,
          email:      i.email || FIRM_INFO.email,
          website:    i.website || FIRM_INFO.website,
          iapd_url:   i.iapd_url || FIRM_INFO.iapd_url,
          cco_name:   i.cco_name || "",
          social_url:   i.social_url || "",
          social_label: i.social_label || "",
          logo:         i.logo || "",
          advUrl:     d?.adv_url || i.iapd_url || FIRM_INFO.iapd_url,
          privacyUrl: d?.privacy_notice_url || "/privacy",
        });
      })
      .catch(() => { /* keep generic fallback */ });
    return () => { alive = false; };
  }, []);
  return id;
}

// The firm's website as a link: `href` with a scheme, `host` for display
// ("example.com"). Both empty when the firm hasn't set one.
export function firmWebsiteLink(website?: string | null): { href: string; host: string } {
  const w = (website || "").trim();
  if (!w) return { href: "", host: "" };
  const href = /^https?:\/\//i.test(w) ? w : `https://${w}`;
  return { href, host: href.replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/$/, "") };
}

export function FooterDisclaimer() {
  const FIRM = useFirmIdentity();
  const { advUrl, privacyUrl } = FIRM;
  return (
    <div style={{
      padding: "12px 18px",
      borderTop: "1px solid #F3F4F6",
      background: "white",
      display: "flex",
      flexWrap: "wrap",
      gap: "10px 16px",
      alignItems: "center",
      justifyContent: "space-between",
      fontSize: "10.5px",
      color: "#6B7280",
      lineHeight: 1.45,
    }}>
      <p style={{ margin: 0, flex: 1, minWidth: "260px", fontStyle: "italic" }}>
        Provided by <strong style={{ color: "#374151" }}>{FIRM.short_name}</strong>, a {FIRM.state}-registered investment advisor (CRD #{FIRM.crd}).
        Information shown is for planning purposes only — not investment advice or a guarantee of future results.
      </p>
      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", flexShrink: 0 }}>
        <a
          href={advUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#6B7280", textDecoration: "none", fontWeight: 600 }}
        >
          Form ADV ↗
        </a>
        <a
          href="#disclosures"
          onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("ap:open-disclosures")); }}
          style={{ color: "#6B7280", textDecoration: "none", fontWeight: 600 }}
        >
          Disclosures
        </a>
        <a
          href={privacyUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#6B7280", textDecoration: "none", fontWeight: 600 }}
        >
          Privacy
        </a>
        <a
          href={`mailto:${FIRM.email}`}
          style={{ color: "#6B7280", textDecoration: "none", fontWeight: 600 }}
        >
          Contact
        </a>
      </div>
    </div>
  );
}

export function ProspectPortalFooter() {
  const FIRM = useFirmIdentity();
  const { advUrl, privacyUrl } = FIRM;
  // Pre-engagement footer for the Prospect Portal. Legally distinct from
  // the client-side FooterDisclaimer in three ways:
  //   1. No advisory relationship exists yet — the portal is exploratory,
  //      not a service deliverable. Must be explicit (federal Advisers Act
  //      precedent, CA Corp Code §25230).
  //   2. No Form ADV has been delivered yet (that happens at engagement).
  //      We still link the public IAPD page so the prospect can read 2A
  //      before signing anything.
  //   3. No client-relationship warm tone — friendly but business-clear.
  //
  // Required legal elements present below:
  //   • Identity + state-registration disclosure (CRD shown)
  //   • Section 208 / CA §25230 anti-skill-implication boilerplate
  //   • Not-investment-advice + risk-of-loss disclosure
  //   • Link to Form ADV Part 2A (public IAPD page)
  //   • Link to Privacy Notice (CCPA + GLBA Reg S-P)
  //   • Contact path
  return (
    <footer style={{
      maxWidth: "880px",
      margin: "0 auto",
      padding: "28px 24px 36px",
      fontSize: "11px",
      color: "#6B7280",
      lineHeight: 1.55,
    }}>
      <div style={{
        background: "#FFFDF8",
        border: "1px solid #F0EAD6",
        borderRadius: "8px",
        padding: "16px 18px",
      }}>
        <p style={{ margin: 0, fontStyle: "italic", color: "#6B7280" }}>
          A few important things while you're exploring whether to work with us.
        </p>
        <ul style={{ margin: "10px 0 0", padding: "0 0 0 18px", color: "#6B7280", display: "grid", gap: "6px" }}>
          <li>
            This portal is provided by <strong style={{ color: "#374151" }}>{FIRM.legal_name}</strong>, a {FIRM.state}-registered
            investment advisor (CRD&nbsp;#{FIRM.crd}). It helps us evaluate whether to work together. It is not, by itself, an advisory relationship. An advisory relationship begins only when you sign a formal Advisory Agreement.
          </li>
          <li>
            Anything shown here is general financial information. It is not personalized investment advice, a recommendation to buy or sell any security, or an offer or solicitation. All investing involves risk, including the possible loss of principal. Past performance and projections are estimates, not promises.
          </li>
          <li>
            Registration as an investment advisor does not imply a certain level of skill or training, nor any endorsement by the State of California or any federal agency.
          </li>
          <li>
            Information you share with us is stored securely and is never sold. See our Privacy Notice for how we collect, use, and protect it (including your rights as a California resident).
          </li>
        </ul>
      </div>

      <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "10px 18px",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: "14px",
        padding: "0 4px",
      }}>
        <span style={{ color: "#9CA3AF" }}>
          {FIRM.legal_name} · {FIRM.address}
        </span>
        <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
          <a
            href={advUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#6B7280", textDecoration: "none", fontWeight: 600 }}
          >Form ADV Part 2A ↗</a>
          <a
            href={privacyUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#6B7280", textDecoration: "none", fontWeight: 600 }}
          >Privacy Notice</a>
          <a
            href={`mailto:${FIRM.email}`}
            style={{ color: "#6B7280", textDecoration: "none", fontWeight: 600 }}
          >Contact</a>
        </div>
      </div>
    </footer>
  );
}

// The full disclosure body — used inside the first-login modal AND the
// "Disclosures" page link. Single source of truth for what we tell clients.
export function DisclosureBody() {
  const FIRM = useFirmIdentity();
  return (
    <div style={{ display: "grid", gap: "16px", color: "#374151", fontSize: "13px", lineHeight: 1.6 }}>
      <p style={{ margin: 0 }}>
        <strong style={{ color: "#111827" }}>Hi! A quick note before we start.</strong>
      </p>
      <p style={{ margin: 0 }}>
        Sourdough is provided by <strong>{FIRM.legal_name}</strong>, a {FIRM.state}-registered
        investment advisor (CRD #{FIRM.crd}). The numbers, charts, and suggestions you'll see in this app are
        meant to spark intentional conversations with your advisor — they aren't investment advice or guarantees of any specific outcome.
      </p>

      <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: "4px", padding: "12px 14px" }}>
        <p style={{ margin: "0 0 8px", fontSize: "11px", fontWeight: 800, color: "#92400E", textTransform: "uppercase", letterSpacing: "0.06em" }}>A few honest things to know</p>
        <ul style={{ margin: 0, padding: "0 0 0 18px", color: "#78350F", lineHeight: 1.6 }}>
          <li>All investing involves risk, <strong>including the possible loss of the money you invest</strong>.</li>
          <li>Past performance and projections are <strong>estimates, not promises</strong>.</li>
          <li>Insights and benchmarks shown are <strong>directional</strong> — talk with your advisor before acting on them.</li>
          <li>Your bank statements and snapshots are stored securely and are <strong>never sold or shared</strong> outside your advisor relationship.</li>
          <li>Registration as an investment advisor does not imply a certain level of skill or training.</li>
        </ul>
      </div>

      <div>
        <p style={{ margin: "0 0 6px", fontSize: "12px", fontWeight: 700, color: "#111827", textTransform: "uppercase", letterSpacing: "0.05em" }}>Methodology, briefly</p>
        <p style={{ margin: 0 }}>
          Spending categorization uses a combination of merchant pattern matching and learned rules. Peer comparisons are anchored on national medians (BLS-style ranges) adjusted for your stated city, family size, and income — they're <strong>directional, not exact</strong>. Investment performance shown reflects values you and your advisor enter; it isn't a custodian-verified return.
        </p>
      </div>

      <div>
        <p style={{ margin: "0 0 6px", fontSize: "12px", fontWeight: 700, color: "#111827", textTransform: "uppercase", letterSpacing: "0.05em" }}>Where to read more</p>
        <p style={{ margin: 0 }}>
          Our full <a href={FIRM.iapd_url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--brand-primary)", fontWeight: 700 }}>Form ADV Part 2A &amp; 2B ↗</a> covers our services, fee schedule, methods of analysis, and material risks. We strongly recommend reading it.
        </p>
        <p style={{ margin: "8px 0 0", fontSize: "12px", color: "#6B7280" }}>
          {FIRM.legal_name} · {FIRM.address} · {FIRM.phone} · <a href={`mailto:${FIRM.email}`} style={{ color: "#6B7280" }}>{FIRM.email}</a>
        </p>
      </div>
    </div>
  );
}
